import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Container, Row, Col, Card, Alert, Modal } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import axiosInstance from '../../services/axiosConfig';
import axios from 'axios';
import { categoryOptions as sharedCategoryOptions, getDefaultFieldsForCategory, isSystemDefinedField } from '../../utils/eventCategories';
import { validateEventForm, validateRegistrationFields } from '../../utils/eventFormValidation';
import { useAuth } from '../../context/AuthContext';

// Formatta una Date in formato locale YYYY-MM-DDTHH:mm per input datetime-local (evita shift UTC di toISOString)
const toLocalDateTimeInput = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const CreateEvent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // UISP theme styles
  const uispStyles = {
    formLabel: { color: "#333" },
    primaryButton: { backgroundColor: "#006e39", borderColor: "#006e39", color: "white" },
    secondaryButton: { backgroundColor: "#555", color: "white", borderColor: "#555" },
    header: { color: "#006e39" }
  };
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    eventDate: '',
    eventEndDate: '',
    startTime: '',
    endTime: '',
    registrationDeadline: '',
    maxParticipants: '',
    category: '',
    scope: 'territorial',
    sda: '',
    price: '',
    isPublic: true,
    image: null,
    documents: [],
    registrationFields: [],
    regulation: null,
    registrationMode: 'team', // 'team' = iscrizione squadre, 'individual' = seleziona soci individuali
    assignedTo: '' // Assegnazione evento a utente UISP (solo admin)
  });
  // Spiegazione modalità iscrizione
  const registrationModeHelp = {
    team: 'Iscrizione squadre: l’associazione iscrive una o più squadre/formazioni/gruppi. Ogni squadra può essere composta da più membri, e la registrazione avviene per gruppo.',
    individual: 'Seleziona soci individuali: l’associazione iscrive i singoli soci partecipanti all’evento. Ogni partecipante viene selezionato individualmente dall’elenco soci.'
  };

  // Opzioni predefinite per le categorie
  const categoryOptions = [
    {
      value: 'danza', label: 'Danza',
      defaultFields: [
        {
          fieldName: 'style',
          fieldLabel: 'Stile',
          fieldType: 'select',
          isRequired: true,
          options: [
            'Danza Classica (Classico, Neoclassico, Danza di Carattere)',
            'Danza Moderna/Jazz (Modern, Modern Jazz, Lyrical Jazz)',
            'Danza Contemporanea',
            'Street Dance e Urban (Hip Hop, Break Dance, Funk, Hype, Elektric Boogie, Contaminazioni urbane, Jumpstyle)',
            'Danze Jazz Internazionali (Boogie Woogie, Lindy Hop, Rock’n’Roll, Rock Acrobatico, Swing Dance, Mixing Blues, Tap Dance)',
            'Danze Internazionali (Danze caraibiche, Danze argentine, Hustle)',
            'Danze Standard e Latine (Valzer Inglese, Tango, Valzer Viennese, Slow Foxtrot, Quick Step, Samba, Cha Cha Cha, Rumba, Paso Doble, Jive)',
            'Danze Nazionali e Folk (Liscio Unificato, Liscio Tradizionale, Danze Popolari, Mazurka, Valzer, Polka, Danze folkloristiche regionali)',
            'Danze di Fantasia'
          ]
        },
        {
          fieldName: 'ageBand',
          fieldLabel: "Categoria / Fascia d'età",
          fieldType: 'select',
          isRequired: true,
          enforceAgeRules: true,
          options: [
            { value: 'Esibizione (3-5 anni)', minAge: 3, maxAge: 5, maxOutOfQuota: 2 },
            { value: 'Predanza 1 (4-6 anni)', minAge: 4, maxAge: 6, maxOutOfQuota: 2 },
            { value: 'Predanza 2 (6-8 anni)', minAge: 6, maxAge: 8, maxOutOfQuota: 2 },
            { value: 'Predanza 3 (8-10 anni)', minAge: 8, maxAge: 10, maxOutOfQuota: 2 },
            { value: 'Allievi (10-12 anni)', minAge: 10, maxAge: 12, maxOutOfQuota: 2 },
            { value: 'Junior (12-14 anni)', minAge: 12, maxAge: 14, maxOutOfQuota: 2 },
            { value: 'Senior (15-17 anni)', minAge: 15, maxAge: 17, maxOutOfQuota: 2 },
            { value: 'Over (18-30 anni)', minAge: 18, maxAge: 30, maxOutOfQuota: 2 }
          ]
        },
        {
          fieldName: 'category',
          fieldLabel: 'Categoria (Configurazione)',
          fieldType: 'select',
          isRequired: true,
          options: [
            'Solisti',
            'Duo/Passo a due',
            'Gruppi (minimo tre partecipanti)'
          ]
        },
        {
          fieldName: 'choreographyTitle',
          fieldLabel: 'Titolo coreografia',
          fieldType: 'text',
          isRequired: true
        },
        {
          fieldName: 'choreographyDuration',
          fieldLabel: 'Durata coreografia/tempo',
          fieldType: 'text',
          isRequired: true
        },
        {
          fieldName: 'audioFile',
          fieldLabel: 'File audio',
          fieldType: 'audio',
          isRequired: true
        }
      ]
    },
    {
      value: 'manifestazione', label: 'Manifestazione',
      defaultFields: [
        { fieldName: 'athleteName', fieldLabel: 'Nome Atleta', fieldType: 'text', isRequired: true }
      ]
    },
    { value: 'stage', label: 'Stage', defaultFields: [] },
    { value: 'acquaviva', label: 'Acquaviva', defaultFields: [] },
    { value: 'atletica leggera', label: 'Atletica Leggera', defaultFields: [] },
    { value: 'calcio', label: 'Calcio', defaultFields: [] },
    { value: 'ciclismo', label: 'Ciclismo', defaultFields: [] },
    { value: 'discipline orientali', label: 'Discipline Orientali', defaultFields: [] },
    { value: 'equestri e cinofile', label: 'Equestri e Cinofile', defaultFields: [] },
    { value: 'ginnastiche', label: 'Ginnastiche', defaultFields: [] },
    { value: 'giochi', label: 'Giochi', defaultFields: [] },
    { value: 'montagna', label: 'Montagna', defaultFields: [] },
    { value: 'motorismo', label: 'Motorismo', defaultFields: [] },
    { value: 'neve', label: 'Neve', defaultFields: [] },
    { value: 'nuoto', label: 'Nuoto', defaultFields: [] },
    { value: 'pallacanestro', label: 'Pallacanestro', defaultFields: [] },
    { value: 'pallavolo', label: 'Pallavolo', defaultFields: [] },
    { value: 'pattinaggio', label: 'Pattinaggio', defaultFields: [] },
    { value: 'subacquea', label: 'Subacquea', defaultFields: [] },
    { value: 'tennis', label: 'Tennis', defaultFields: [] },
    { value: 'vela', label: 'Vela', defaultFields: [] },
    { value: 'multi-sport', label: 'Multi-Sport', defaultFields: [] },
    { value: 'corso', label: 'Corso', defaultFields: [] },
    { value: 'altro', label: 'Altro', defaultFields: [] }
  ];

  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationQuery, setLocationQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const searchTimeoutRef = useRef(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uispUsers, setUispUsers] = useState([]);
  const [conflictEvents, setConflictEvents] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // Carica gli utenti UISP per l'assegnazione (solo per admin)
  useEffect(() => {
    const fetchUispUsers = async () => {
      if (user && user.role === 'admin') {
        try {
          const response = await axiosInstance.get('/admin/users/uisp');
          setUispUsers(response.data.users || []);
        } catch (error) {
          console.error('Errore nel caricamento degli utenti UISP:', error);
          setUispUsers([]);
        }
      }
    };
    fetchUispUsers();
  }, [user]);

  // Per gli utenti SDA, imposta automaticamente la categoria alla loro disciplina + campi default (solo se non è già impostata)
  useEffect(() => {
    if (user && user.role && user.role.startsWith('sda_') && user.discipline && !formData.category) {
      console.log('SDA user detected, setting default category to:', user.discipline);
      const selectedCategory = categoryOptions.find(cat => cat.value === user.discipline);
      setFormData(prev => ({
        ...prev,
        category: user.discipline,
        registrationFields: selectedCategory ? [...selectedCategory.defaultFields] : prev.registrationFields,
        registrationMode: user.discipline === 'danza' ? 'team' : prev.registrationMode
      }));
    }
  }, [user, formData.category]);

  const handleChange = async (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'file') {
      if (name === 'documents') {
        setFormData({
          ...formData,
          [name]: Array.from(files)
        });
      } else if (name === 'regulation') {
        setFormData({
          ...formData,
          regulation: files[0] || null
        });
      } else {
        setFormData({
          ...formData,
          [name]: files[0]
        });
      }
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      // Se cambia la data dell'evento, calcola automaticamente la deadline (3 giorni prima, ora locale)
      if (name === 'eventDate' && value) {
        const eventDate = new Date(value + 'T12:00:00');
        const deadline = new Date(eventDate);
        deadline.setDate(deadline.getDate() - 3);
        deadline.setHours(23, 59, 0, 0);
        const deadlineStr = toLocalDateTimeInput(deadline); // Formato datetime-local
        setFormData({
          ...formData,
          [name]: value,
          eventEndDate: formData.eventEndDate && formData.eventEndDate < value ? value : (formData.eventEndDate || value),
          registrationDeadline: deadlineStr
        });
        // Controlla se esistono già eventi in quella giornata
        try {
          const checkRes = await axiosInstance.get(`/api/events/check-date?date=${value}`);
          const events = checkRes.data.events || [];
          if (events.length > 0) {
            setConflictEvents(events);
            setShowConflictModal(true);
          } else {
            setConflictEvents([]);
          }
        } catch (checkErr) {
          console.warn('Impossibile verificare conflitti di data:', checkErr);
        }
        return;
      }
      
      // Se cambia la categoria, aggiorna anche modalità iscrizione + campi default in un unico update atomico
      if (name === 'category') {
        let newMode = formData.registrationMode;
        // Categorie che usano l'iscrizione a squadre
        if (value === 'danza' || value === 'calcio' || value === 'pallacanestro' || value === 'pallavolo') {
          newMode = 'team';
        }
        // Categorie che usano l'iscrizione individuale
        else if (value === 'manifestazione' || value === 'atletica leggera' || value === 'ciclismo' || value === 'tennis') {
          newMode = 'individual';
        }
        // Usa i default centralizzati (con regole età danza); fallback alla lista locale per gli sport senza default
        const sharedDefaults = getDefaultFieldsForCategory(value);
        const selectedCategory = categoryOptions.find(cat => cat.value === value);
        const fieldsToApply = sharedDefaults.length > 0
          ? sharedDefaults
          : (selectedCategory ? [...selectedCategory.defaultFields] : null);
        setFormData(prev => ({
          ...prev,
          [name]: value,
          registrationMode: newMode,
          ...(fieldsToApply ? { registrationFields: fieldsToApply } : {})
        }));
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    }
    // --- UI: selezione modalità iscrizione ---
    // Da inserire nel form, sopra i campi evento
    // <Form.Group className="mb-3">
    //   <Form.Label style={uispStyles.formLabel}>Modalità iscrizione <span style={{color:'red'}}>*</span></Form.Label>
    //   <div>
    //     <Form.Check
    //       inline
    //       type="radio"
    //       id="registrationMode-team"
    //       label="Iscrizione squadre"
    //       name="registrationMode"
    //       value="team"
    //       checked={formData.registrationMode === 'team'}
    //       onChange={handleChange}
    //     />
    //     <Form.Check
    //       inline
    //       type="radio"
    //       id="registrationMode-individual"
    //       label="Seleziona soci individuali"
    //       name="registrationMode"
    //       value="individual"
    //       checked={formData.registrationMode === 'individual'}
    //       onChange={handleChange}
    //     />
    //   </div>
    //   <div className="mt-2">
    //     <small className="text-muted">
    //       {registrationModeHelp[formData.registrationMode]}
    //     </small>
    //   </div>
    // </Form.Group>
  };

  // Gestisce l'aggiunta di un nuovo campo di iscrizione
  const handleAddField = () => {
    setFormData(prevData => ({
      ...prevData,
      registrationFields: [
        ...prevData.registrationFields,
        { fieldName: '', fieldLabel: '', fieldType: 'text', isRequired: false, options: [] }
      ]
    }));
  };

  // Gestisce la rimozione di un campo di iscrizione
  const handleRemoveField = (index) => {
    const updatedFields = [...formData.registrationFields];
    updatedFields.splice(index, 1);
    setFormData({
      ...formData,
      registrationFields: updatedFields
    });
  };

  // Gestisce la modifica di un campo di iscrizione
  const handleFieldChange = (index, field, value) => {
    const updatedFields = [...formData.registrationFields];
    updatedFields[index] = {
      ...updatedFields[index],
      [field]: value
    };

    // Se il tipo del campo è cambiato a 'select' o 'multiselect', inizializziamo le opzioni
    if (field === 'fieldType' && (value === 'select' || value === 'multiselect') && !updatedFields[index].options) {
      updatedFields[index].options = [''];
    }

    // Se il campo è di tipo file o audio e viene aggiunto un file
    if (field === 'file' && value) {
      console.log(`File added to field ${index}:`, value);
    }

    setFormData({
      ...formData,
      registrationFields: updatedFields
    });
  };

  // Gestisce l'aggiunta di una opzione per i campi select/multiselect
  const handleAddOption = (fieldIndex) => {
    const updatedFields = [...formData.registrationFields];
    if (!updatedFields[fieldIndex].options) {
      updatedFields[fieldIndex].options = [''];
    } else {
      updatedFields[fieldIndex].options.push('');
    }

    setFormData({
      ...formData,
      registrationFields: updatedFields
    });
  };

  // Gestisce la modifica di una opzione per i campi select/multiselect
  const handleOptionChange = (fieldIndex, optionIndex, value) => {
    const updatedFields = [...formData.registrationFields];
    updatedFields[fieldIndex].options[optionIndex] = value;

    setFormData({
      ...formData,
      registrationFields: updatedFields
    });
  };

  // Gestisce la rimozione di una opzione per i campi select/multiselect
  const handleRemoveOption = (fieldIndex, optionIndex) => {
    const updatedFields = [...formData.registrationFields];
    updatedFields[fieldIndex].options.splice(optionIndex, 1);

    setFormData({
      ...formData,
      registrationFields: updatedFields
    });
  };
  // Funzione per cercare indirizzi utilizzando OpenStreetMap Nominatim
  const searchLocations = async (query) => {
    if (!query || query.length < 3) {
      setLocationSuggestions([]);
      return;
    }

    setIsLoading(true);

    try {
      // Usiamo l'API di OpenStreetMap Nominatim per la ricerca degli indirizzi
      const response = await axios.get(`https://nominatim.openstreetmap.org/search`, {
        params: {
          q: query,
          format: 'json',
          addressdetails: 1,
          limit: 5
        },
        headers: {
          'Accept-Language': 'it' // Risultati in italiano
        }
      });

      const suggestions = response.data.map(item => ({
        id: item.place_id,
        display_name: item.display_name,
        lat: item.lat,
        lon: item.lon
      }));

      setLocationSuggestions(suggestions);
    } catch (error) {
      console.error('Errore nella ricerca degli indirizzi:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Gestisce il cambio del campo di ricerca indirizzo con debounce
  const handleLocationSearch = (e) => {
    const query = e.target.value;
    setLocationQuery(query);

    // Implementazione di debounce per limitare le richieste API
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchLocations(query);
    }, 500);
  };

  // Gestisce la selezione di un indirizzo dalla lista dei suggerimenti
  const handleSelectLocation = (location) => {
    setSelectedLocation(location);
    setLocationQuery(location.display_name);
    setFormData({
      ...formData,
      location: location.display_name
    });
    setLocationSuggestions([]);
  };

  const handleSubmit = async (e) => {
    console.log('Form submit triggered');
    e.preventDefault();
    setError('');

    try {
      // Validate location
      if (!formData.location || formData.location.trim() === '') {
        setError('La location è obbligatoria. Cerca e seleziona un indirizzo dalla lista.');
        return;
      }

      // Validazione centralizzata (titolo, date, orari, categoria, livello, numeri)
      const formCheck = validateEventForm(formData);
      if (!formCheck.isValid) {
        setError(formCheck.errors[0]);
        return;
      }
      if (formData.registrationFields && formData.registrationFields.length > 0) {
        const fieldsCheck = validateRegistrationFields(formData.registrationFields);
        if (!fieldsCheck.isValid) {
          setError(fieldsCheck.errors[0]);
          return;
        }
      }

      // Update valid field types to include 'audio' and 'file'
      const validFieldTypes = ['text', 'number', 'select', 'multiselect', 'audio', 'file'];
      let sanitizedFormData = { ...formData };

      if (sanitizedFormData.registrationFields && sanitizedFormData.registrationFields.length > 0) {
        sanitizedFormData.registrationFields = sanitizedFormData.registrationFields.map(field => {
          if (!validFieldTypes.includes(field.fieldType)) {
            // Convert unsupported field types to 'text'
            console.log(`Converting field type '${field.fieldType}' to 'text' for field '${field.fieldName}'`);
            return { ...field, fieldType: 'text' };
          }
          return field;
        });
      } const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      // Create FormData object for submission
      let dataToSend = new FormData();

      // Log form data for debugging
      console.log('Form data being submitted:', {
        title: sanitizedFormData.title,
        description: sanitizedFormData.description,
        location: sanitizedFormData.location,
        eventDate: sanitizedFormData.eventDate,
        eventEndDate: sanitizedFormData.eventEndDate,
        startTime: sanitizedFormData.startTime,
        endTime: sanitizedFormData.endTime,
        maxParticipants: sanitizedFormData.maxParticipants,
        price: sanitizedFormData.price,
        category: sanitizedFormData.category,
        isPublic: sanitizedFormData.isPublic,
        registrationMode: sanitizedFormData.registrationMode,
        registrationFields: sanitizedFormData.registrationFields,
        regulation: sanitizedFormData.regulation
      });

      // Add basic text fields
      dataToSend.append('title', sanitizedFormData.title);
      dataToSend.append('description', sanitizedFormData.description);
      dataToSend.append('location', sanitizedFormData.location);
      dataToSend.append('eventDate', sanitizedFormData.eventDate);
      dataToSend.append('eventEndDate', sanitizedFormData.eventEndDate || sanitizedFormData.eventDate);
      dataToSend.append('startTime', sanitizedFormData.startTime);
      dataToSend.append('endTime', sanitizedFormData.endTime);
      dataToSend.append('registrationDeadline', sanitizedFormData.registrationDeadline);
      dataToSend.append('maxParticipants', sanitizedFormData.maxParticipants ? Number(sanitizedFormData.maxParticipants) : 0);
      dataToSend.append('price', sanitizedFormData.price ? Number(sanitizedFormData.price) : 0);
      dataToSend.append('category', sanitizedFormData.category);
      dataToSend.append('scope', sanitizedFormData.scope || 'territorial');
      if (sanitizedFormData.sda) dataToSend.append('sda', sanitizedFormData.sda);
      dataToSend.append('isPublic', sanitizedFormData.isPublic);
      dataToSend.append('registrationMode', sanitizedFormData.registrationMode);
      
      // Add assignedTo if present (admin only feature)
      if (sanitizedFormData.assignedTo) {
        dataToSend.append('assignedTo', sanitizedFormData.assignedTo);
      }

      // Add regulation document as first document if exists
      if (sanitizedFormData.regulation) {
        // Crea un oggetto File con nome identificativo se possibile
        // Se il backend accetta solo file, aggiungi come primo documento
        dataToSend.append('documents', sanitizedFormData.regulation, `Regolamento_${sanitizedFormData.regulation.name}`);
      }

      // Handle registration fields properly - stringify the array to ensure it's sent correctly
      if (sanitizedFormData.registrationFields && sanitizedFormData.registrationFields.length > 0) {
        const regFieldsJson = JSON.stringify(sanitizedFormData.registrationFields);
        console.log('Registration fields JSON:', regFieldsJson);
        dataToSend.append('registrationFields', regFieldsJson);
      }

      // Add image if exists
      if (sanitizedFormData.image) {
        dataToSend.append('image', sanitizedFormData.image);
      }

      // Add other documents if they exist
      if (sanitizedFormData.documents && sanitizedFormData.documents.length > 0) {
        Array.from(sanitizedFormData.documents).forEach(doc => {
          dataToSend.append('documents', doc);
        });
      }

      // Process custom fields for audio and document files
      if (sanitizedFormData.registrationFields && sanitizedFormData.registrationFields.length > 0) {
        sanitizedFormData.registrationFields.forEach((field, index) => {
          if (field.file) {
            const fieldType = field.fieldType === 'audio' ? 'customFieldAudio' : 'customFieldFile';
            dataToSend.append(`${fieldType}_${index}`, field.file);
            // Add metadata for the backend
            dataToSend.append(`${fieldType}_${index}_metadata`, JSON.stringify({
              fieldName: field.fieldName,
              fieldLabel: field.fieldLabel,
              fieldType: field.fieldType,
              isRequired: field.isRequired
            }));
          }
        });
      }

      console.log('FormData entries:');
      for (let pair of dataToSend.entries()) {
        console.log(pair[0] + ': ' + (pair[0] === 'registrationFields' ? pair[1] : typeof pair[1]));
      }

      // Make a double-check of registrationFields content
      const regFieldsContent = dataToSend.get('registrationFields');
      if (regFieldsContent) {
        try {
          const parsed = JSON.parse(regFieldsContent);
          console.log('Parsed registrationFields:', parsed);
          // Check for any invalid field types
          const invalidFields = parsed.filter(field => !['text', 'number', 'select', 'multiselect', 'audio', 'file'].includes(field.fieldType));
          if (invalidFields.length > 0) {
            console.error('Warning: Found invalid field types after sanitization:', invalidFields);
          }
        } catch (err) {
          console.error('Error parsing registrationFields:', err);
        }
      }

      console.log('Submitting event data to server...');
      await axiosInstance.post('/api/events', dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/admin/events/list');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating event. Please try again.');
      console.error('Error creating event:', err);
    }
  };

  return (
    <div className="container-fluid min-vh-100" style={{ backgroundColor: '#ffffff' }} >
      <div className="row p-4">
        <div className="col-12 col-md-8 col-lg-6 mx-auto">
          <div className="glass-container p-4 rounded-4" style={{ backgroundColor: 'white', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
            <h2 style={uispStyles.header} className="mb-4">Crea nuovo evento</h2>
            {success ? (
              <div className="alert alert-success">Evento creato con successo! Redirecting...</div>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="mb-3">
                  <label className="form-label" style={uispStyles.formLabel}>Titolo evento</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label" style={uispStyles.formLabel}>Descrizione</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" style={uispStyles.formLabel}>Regolamento</label>
                  <input
                    type="file"
                    className="form-control"
                    name="regulation"
                    onChange={handleChange}
                    accept=".pdf,.doc,.docx,.txt"
                  />
                  <small style={{ color: "#666666" }}>
                    Carica il regolamento dell'evento (PDF, Word, TXT, opzionale)
                  </small>
                  {formData.regulation && (
                    <div className="mt-2">
                      <span className="badge bg-info">Documento selezionato: {formData.regulation.name}</span>
                      <span className="ms-2 text-muted">{(formData.regulation.size / 1024).toFixed(1)} KB</span>
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label" style={uispStyles.formLabel}>Location</label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control"
                      name="locationSearch"
                      value={locationQuery}
                      onChange={handleLocationSearch}
                      placeholder="Cerca indirizzo..."
                      autoComplete="off"
                      required
                    />
                    {isLoading && (
                      <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                        <div className="spinner-border spinner-border-sm text-secondary" role="status">
                          <span className="visually-hidden">Caricamento...</span>
                        </div>
                      </div>
                    )}

                    {locationSuggestions.length > 0 && (
                      <div className="position-absolute w-100 mt-1 shadow-sm border rounded bg-white z-3">
                        <ul className="list-group list-group-flush">
                          {locationSuggestions.map(location => (
                            <li
                              key={location.id}
                              className="list-group-item list-group-item-action py-2 px-3 cursor-pointer"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleSelectLocation(location)}
                            >
                              <small>{location.display_name}</small>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Campo nascosto per mantenere la compatibilità con la validazione del form */}
                  <input
                    type="hidden"
                    name="location"
                    value={formData.location}
                    required
                  />

                  {selectedLocation && (
                    <div className="mt-2">
                      <span className="badge bg-success">Indirizzo selezionato: {selectedLocation.display_name}</span>
                      <div className="mt-1">
                        <small className="text-muted">
                          Coordinate: {selectedLocation.lat}, {selectedLocation.lon}
                        </small>
                      </div>
                    </div>
                  )}
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={uispStyles.formLabel}>Data inizio evento</label>
                    <input
                      type="date"
                      className="form-control"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={uispStyles.formLabel}>Data fine evento</label>
                    <input
                      type="date"
                      className="form-control"
                      name="eventEndDate"
                      value={formData.eventEndDate}
                      onChange={handleChange}
                      min={formData.eventDate || undefined}
                      required
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={uispStyles.formLabel}>Orario inizio</label>
                    <input
                      type="time"
                      className="form-control"
                      name="startTime"
                      value={formData.startTime}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={uispStyles.formLabel}>Orario fine</label>
                    <input
                      type="time"
                      className="form-control"
                      name="endTime"
                      value={formData.endTime}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label className="form-label" style={uispStyles.formLabel}>
                      Chiusura iscrizioni <span style={{color:'red'}}>*</span>
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      name="registrationDeadline"
                      value={formData.registrationDeadline}
                      onChange={handleChange}
                      required
                    />
                    <Form.Text className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      Data e ora di chiusura delle iscrizioni. Impostata automaticamente 3 giorni prima dell'evento, ma può essere modificata.
                    </Form.Text>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={uispStyles.formLabel}>Partecipanti massimi</label>
                    <input
                      type="number"
                      className="form-control"
                      name="maxParticipants"
                      value={formData.maxParticipants}
                      onChange={handleChange}
                      min="1"
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={uispStyles.formLabel}>Prezzo (€)</label>
                    <input
                      type="number"
                      className="form-control"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>
                {/* Modalità iscrizione: team o individuale */}
                <div className="mb-3">
                  <label className="form-label" style={uispStyles.formLabel}>
                    Modalità iscrizione <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="registrationMode-team"
                        name="registrationMode"
                        value="team"
                        checked={formData.registrationMode === 'team'}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="registrationMode-team">
                        Iscrizione squadre
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="registrationMode-individual"
                        name="registrationMode"
                        value="individual"
                        checked={formData.registrationMode === 'individual'}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="registrationMode-individual">
                        Seleziona soci individuali
                      </label>
                    </div>
                  </div>
                  <div className="mt-2">
                    <small className="text-muted">
                      {registrationModeHelp[formData.registrationMode]}
                    </small>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label" style={uispStyles.formLabel}>Categoria</label>
                  <select
                    className="form-select"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Seleziona una categoria</option>
                    {user && user.role && user.role.startsWith('sda_') ? (
                      // Per utenti SDA: mostra solo la loro disciplina, manifestazione e stage
                      <>
                        {user.discipline && (
                          <option value={user.discipline}>
                            {categoryOptions.find(opt => opt.value === user.discipline)?.label || user.discipline.charAt(0).toUpperCase() + user.discipline.slice(1)}
                          </option>
                        )}
                        <option value="manifestazione">Manifestazione</option>
                        <option value="stage">Stage</option>
                      </>
                    ) : (
                      // Per altri utenti: mostra tutte le categorie
                      categoryOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))
                    )}
                  </select>
                  {user && user.role && user.role.startsWith('sda_') && (
                    <Form.Text className="text-info">
                      <i className="bi bi-info-circle me-1"></i>
                      Come responsabile SDA, puoi creare eventi nella categoria "{user.discipline}", "Manifestazione" o "Stage".
                    </Form.Text>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label" style={uispStyles.formLabel}>SDA / Disciplina (opzionale)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="sda"
                    value={formData.sda || ''}
                    onChange={handleChange}
                    placeholder="Es. danza, judo..."
                    maxLength={100}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label" style={uispStyles.formLabel}>Livello evento <span style={{ color: 'red' }}>*</span></label>
                  <select
                    className="form-select"
                    name="scope"
                    value={formData.scope}
                    onChange={handleChange}
                    required
                  >
                    <option value="territorial">Territoriale</option>
                    <option value="regional">Regionale</option>
                    <option value="national">Nazionale</option>
                  </select>
                  <small className="text-muted">
                    Il livello viene mostrato alle associazioni nel dettaglio evento.
                  </small>
                </div>

                {/* Campo Assegnazione - Visibile solo per admin */}
                {user && user.role === 'admin' && (
                  <div className="mb-3">
                    <label className="form-label" style={uispStyles.formLabel}>Assegna evento a</label>
                    <select
                      className="form-select"
                      name="assignedTo"
                      value={formData.assignedTo}
                      onChange={handleChange}
                    >
                    <option value="">Nessuna assegnazione (pubblico)</option>
                    {uispUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.username} - {user.email || 'email non disponibile'} ({
                          user.role === 'uisp_national'
                            ? 'UISP Nazionale'
                            : user.role === 'uisp_regional'
                              ? 'UISP Regionale'
                              : 'UISP Territoriale'
                        })
                      </option>
                    ))}
                    </select>
                    <small className="text-muted">
                      Seleziona l'utente UISP che gestirà questo evento
                    </small>
                  </div>
                )}

                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isPublic"
                    name="isPublic"
                    checked={formData.isPublic}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" style={uispStyles.formLabel} htmlFor="isPublic">
                    Evento pubblico (visibile a tutti gli utenti)
                  </label>
                </div>                <div className="mb-3">
                  <label className="form-label" style={uispStyles.formLabel}>Immagine evento</label>
                  <input
                    type="file"
                    className="form-control"
                    name="image"
                    onChange={handleChange}
                    accept="image/*"
                  />
                  <small style={{ color: "#666666" }}>Opzionale: Carica un'immagine per il tuo evento</small>
                </div>

                <div className="mb-3">
                  <label className="form-label" style={uispStyles.formLabel}>Documenti</label>
                  <input
                    type="file"
                    className="form-control"
                    name="documents"
                    onChange={handleChange}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                    multiple
                  />
                  <small style={{ color: "#666666" }}>
                    Opzionale: Carica documenti aggiuntivi relativi all'evento (PDF, Word, Excel, ecc.).
                    Puoi selezionare fino a 5 file.
                  </small>

                  {formData.documents && formData.documents.length > 0 && (
                    <div className="mt-2">
                      <p style={{ color: "#006e39" }}>Documenti selezionati:</p>
                      <ul className="list-group">
                        {Array.from(formData.documents).map((file, index) => (
                          <li className="list-group-item d-flex justify-content-between align-items-center" key={index}>
                            <span>
                              <i className="bi bi-file-earmark me-2"></i>
                              {file.name}
                            </span>
                            <span className="badge bg-secondary">{(file.size / 1024).toFixed(1)} KB</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Sezione per i campi di iscrizione personalizzati */}
                <div className="mb-4">
                  <h4 style={uispStyles.header} className="mb-3">
                    <i className="bi bi-list-check me-2"></i>
                    Campi di iscrizione personalizzati
                  </h4>
                  <p className="text-muted mb-3">
                    Configura quali informazioni dovranno essere inserite durante l'iscrizione all'evento.
                  </p>

                  {formData.registrationFields.length > 0 ? (
                    <div className="mb-3">
                      {formData.registrationFields.map((field, index) => (
                        <div key={index} className="card mb-3">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h5 className="card-title mb-0">Campo #{index + 1}</h5>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoveField(index)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>

                            <div className="row mb-2">
                              <div className="col-md-6">
                                <label className="form-label">Nome campo (identificativo)</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={field.fieldName}
                                  onChange={(e) => handleFieldChange(index, 'fieldName', e.target.value)}
                                  required
                                  readOnly={isSystemDefinedField(field.fieldName)}
                                  style={isSystemDefinedField(field.fieldName) ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                  title={isSystemDefinedField(field.fieldName) ? 'Questo campo identificativo non può essere modificato' : ''}
                                />
                                {isSystemDefinedField(field.fieldName) && (
                                  <small className="text-muted ms-1">Campo identificativo predefinito, non modificabile</small>
                                )}
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Etichetta (visibile all'utente)</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={field.fieldLabel}
                                  onChange={(e) => handleFieldChange(index, 'fieldLabel', e.target.value)}
                                  required
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <div className="col-md-6">
                                <label className="form-label">Tipo campo</label>
                                <select
                                  className="form-select"
                                  value={field.fieldType}
                                  onChange={(e) => handleFieldChange(index, 'fieldType', e.target.value)}
                                >
                                  <option value="text">Testo</option>
                                  <option value="number">Numero</option>
                                  <option value="select">Selezione singola</option>
                                  <option value="multiselect">Selezione multipla</option>
                                  <option value="file">File Documento</option>
                                  <option value="audio">Audio/Musica (per partecipanti)</option>
                                </select>
                              </div>
                              <div className="col-md-6 d-flex align-items-center">
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`required-${index}`}
                                    checked={field.isRequired}
                                    onChange={(e) => handleFieldChange(index, 'isRequired', e.target.checked)}
                                  />
                                  <label className="form-check-label" htmlFor={`required-${index}`}>
                                    Campo obbligatorio
                                  </label>
                                </div>
                              </div>
                            </div>

                            {/* Opzioni per i campi select/multiselect */}
                            {(field.fieldType === 'select' || field.fieldType === 'multiselect') && (
                              <div className="mt-3">
                                <label className="form-label">Opzioni</label>
                                {field.options && field.options.map((option, optionIndex) => (
                                  <div key={optionIndex} className="input-group mb-2">
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={option}
                                      onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                                      placeholder={`Opzione ${optionIndex + 1}`}
                                      required
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger"
                                      onClick={() => handleRemoveOption(index, optionIndex)}
                                    >
                                      <i className="bi bi-x"></i>
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => handleAddOption(index)}
                                >
                                  <i className="bi bi-plus-circle me-1"></i>
                                  Aggiungi opzione
                                </button>
                              </div>
                            )}

                            {/* Input per file audio o documenti */}
                            {(field.fieldType === 'audio' || field.fieldType === 'file') && (
                              <div className="mt-3">
                                <div className={field.fieldType === 'audio' ? "border border-primary rounded p-3" : "border rounded p-3"}>
                                  <div className={field.fieldType === 'audio' ? "bg-primary text-white p-2 mb-3 rounded" : "bg-light p-2 mb-3 rounded"}>
                                    <div className="d-flex align-items-center">
                                      {field.fieldType === 'audio' ? (
                                        <>
                                          <i className="bi bi-music-note-beamed me-2"></i>
                                          <span><strong>File Audio/Musica</strong></span>
                                        </>
                                      ) : (
                                        <>
                                          <i className="bi bi-file-earmark me-2"></i>
                                          <span><strong>File Documento</strong></span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <label className="form-label">
                                    {field.fieldType === 'audio'
                                      ? 'Esempio file audio/musica'
                                      : 'Esempio documento'}
                                  </label>
                                  <input
                                    type="file"
                                    className="form-control"
                                    accept={field.fieldType === 'audio'
                                      ? 'audio/mp3,audio/wav,audio/ogg,audio/mpeg'
                                      : '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt'}
                                    onChange={(e) => handleFieldChange(index, 'file', e.target.files[0])}
                                  />
                                  {field.fieldType === 'audio' && (
                                    <div className="mt-2 alert alert-info py-2">
                                      <i className="bi bi-info-circle me-1"></i>
                                      <span>Questo campo permetterà ai partecipanti di caricare un file audio per questo evento.</span>
                                      <div><small>Formati supportati: MP3, WAV, OGG, MPEG.</small></div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="alert alert-info">
                      <i className="bi bi-info-circle me-2"></i>
                      Non ci sono campi di iscrizione personalizzati. Aggiungi dei campi o seleziona una categoria predefinita.
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-outline-success"
                    onClick={handleAddField}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Aggiungi campo personalizzato
                  </button>
                </div>

                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn"
                    style={uispStyles.secondaryButton}
                    onClick={() => {
                      try {
                        console.log('Annulla button clicked');
                        navigate('/admin/events');
                      } catch (error) {
                        console.error('Error navigating back:', error);
                      }
                    }}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    style={uispStyles.primaryButton}
                    onClick={(e) => {
                      console.log('Crea evento button clicked');
                      // Don't call e.preventDefault() here because we want the form's onSubmit to run
                    }}
                  >
                    Crea evento
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      {/* Modale conflitti di data */}
      <Modal show={showConflictModal} onHide={() => setShowConflictModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#fff3cd', borderBottom: '1px solid #ffc107' }}>
          <Modal.Title style={{ color: '#856404', fontSize: '1.1rem' }}>
            ⚠️ Attenzione: eventi già presenti in questa data
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: '0.75rem' }}>
            Nella giornata selezionata sono già presenti i seguenti eventi:
          </p>
          <ul style={{ paddingLeft: '1.25rem', marginBottom: 0 }}>
            {conflictEvents.map(ev => (
              <li key={ev._id || ev.id} style={{ marginBottom: '0.4rem' }}>
                <strong>{ev.title}</strong>
                {ev.location && <span style={{ color: '#555' }}> — {ev.location}</span>}
                {ev.startTime && <span style={{ color: '#555' }}>, ore {ev.startTime}</span>}
              </li>
            ))}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="warning" onClick={() => setShowConflictModal(false)}>
            Ho capito, continuo comunque
          </Button>
        </Modal.Footer>
      </Modal>
    </div >
  );
};

export default CreateEvent;